const KEY_REDIRECT_TO = 'apropos-jobs__redirect-to'
const KEY_JOB_OFFER_ID = 'apropos-jobs__jobOfferId'
const KEY_READY_TO_SUBMIT_JOB_OFFER_ID = 'apropos-jobs__readyToSubmitJobOfferId'
const KEY_APPLY_SOURCE = 'apropos-jobs__applySource'
const READY_TO_SUBMIT_TTL_MS = 30 * 24 * 60 * 60 * 1000

const safeGetItem = key => {
	try {
		if (typeof window === 'undefined' || !window.localStorage) {
			return null
		}
		return window.localStorage.getItem(key)
	} catch (e) {
		return null
	}
}

const safeSetItem = (key, value) => {
	try {
		if (typeof window === 'undefined' || !window.localStorage) {
			return
		}
		window.localStorage.setItem(key, value)
	} catch (e) {}
}

const safeRemoveItem = key => {
	try {
		if (typeof window === 'undefined' || !window.localStorage) {
			return
		}
		window.localStorage.removeItem(key)
	} catch (e) {}
}

const safeGetSessionItem = key => {
	try {
		if (typeof window === 'undefined' || !window.sessionStorage) {
			return null
		}
		return window.sessionStorage.getItem(key)
	} catch (e) {
		return null
	}
}

const safeSetSessionItem = (key, value) => {
	try {
		if (typeof window === 'undefined' || !window.sessionStorage) {
			return false
		}
		window.sessionStorage.setItem(key, value)
		return true
	} catch (e) {
		return false
	}
}

const safeRemoveSessionItem = key => {
	try {
		if (typeof window === 'undefined' || !window.sessionStorage) {
			return
		}
		window.sessionStorage.removeItem(key)
	} catch (e) {}
}

const parseReadyToSubmitPayload = value => {
	if (!value) {
		return null
	}

	try {
		const data = JSON.parse(value)
		if (typeof data === 'number' || typeof data === 'string') {
			return {
				jobOfferId: String(data),
				createdAt: 0
			}
		}
		if (data && data.jobOfferId && data.createdAt) {
			return {
				jobOfferId: String(data.jobOfferId),
				createdAt: Number(data.createdAt)
			}
		}
	} catch (e) {
		// Older versions stored only the job id as a plain string.
		return {
			jobOfferId: String(value),
			createdAt: 0
		}
	}

	return null
}

const readyToSubmitPayloadIsExpired = payload =>
	!payload || Date.now() - payload.createdAt > READY_TO_SUBMIT_TTL_MS

const serializeReadyToSubmitPayload = payload =>
	JSON.stringify({
		jobOfferId: String(payload.jobOfferId),
		createdAt: Number(payload.createdAt)
	})

// Matches both the direct job route and the SEO apply link whose id trails
// the slug. Kept here so every surface that remembers a job destination
// extracts the id the same way.
export const getJobOfferIdFromRedirectPath = redirectPath => {
	if (typeof redirectPath !== 'string') {
		return null
	}

	const directJobOfferMatch = redirectPath.match(
		/^\/job-offer\/(\d+)(?:[/?#]|$)/
	)

	if (directJobOfferMatch) {
		return directJobOfferMatch[1]
	}

	const applyJobOfferMatch = redirectPath.match(
		/^\/job-offer\/apply\/[^/]+\/[^/]+-(\d+)(?:[/?#]|$)/
	)

	return applyJobOfferMatch ? applyJobOfferMatch[1] : null
}

export const applicationIntent = {
	getRedirectToPath() {
		return safeGetItem(KEY_REDIRECT_TO)
	},
	setRedirectToPath(path) {
		if (typeof path === 'string' && path.length > 0) {
			safeSetItem(KEY_REDIRECT_TO, path)
		}
	},
	clearRedirectToPath() {
		safeRemoveItem(KEY_REDIRECT_TO)
	},

	getJobOfferId() {
		return safeGetItem(KEY_JOB_OFFER_ID)
	},
	setJobOfferId(jobOfferId) {
		if (jobOfferId !== null && jobOfferId !== undefined) {
			safeSetItem(KEY_JOB_OFFER_ID, String(jobOfferId))
		}
	},
	clearJobOfferId() {
		safeRemoveItem(KEY_JOB_OFFER_ID)
	},

	getReadyToSubmitJobOfferId() {
		const payload = parseReadyToSubmitPayload(
			safeGetSessionItem(KEY_READY_TO_SUBMIT_JOB_OFFER_ID)
		)

		if (payload) {
			if (readyToSubmitPayloadIsExpired(payload)) {
				safeRemoveSessionItem(KEY_READY_TO_SUBMIT_JOB_OFFER_ID)
			} else {
				return payload.jobOfferId
			}
		}

		const legacyPayload = parseReadyToSubmitPayload(
			safeGetItem(KEY_READY_TO_SUBMIT_JOB_OFFER_ID)
		)

		if (!legacyPayload) {
			return null
		}

		if (readyToSubmitPayloadIsExpired(legacyPayload)) {
			this.clearReadyToSubmitJobOfferId()
			return null
		}

		const serializedLegacyPayload =
			serializeReadyToSubmitPayload(legacyPayload)
		const migrated = safeSetSessionItem(
			KEY_READY_TO_SUBMIT_JOB_OFFER_ID,
			serializedLegacyPayload
		)
		const storedSessionPayload = parseReadyToSubmitPayload(
			safeGetSessionItem(KEY_READY_TO_SUBMIT_JOB_OFFER_ID)
		)

		if (
			migrated &&
			storedSessionPayload &&
			storedSessionPayload.jobOfferId === legacyPayload.jobOfferId &&
			storedSessionPayload.createdAt === legacyPayload.createdAt
		) {
			safeRemoveItem(KEY_READY_TO_SUBMIT_JOB_OFFER_ID)
		}

		return legacyPayload.jobOfferId
	},
	setReadyToSubmitJobOfferId(jobOfferId) {
		if (jobOfferId !== null && jobOfferId !== undefined) {
			safeSetSessionItem(
				KEY_READY_TO_SUBMIT_JOB_OFFER_ID,
				serializeReadyToSubmitPayload({
					jobOfferId: String(jobOfferId),
					createdAt: Date.now()
				})
			)
		}
	},
	isReadyToSubmitJobOffer(jobOfferId) {
		const readyJobId = this.getReadyToSubmitJobOfferId()
		return !!(
			readyJobId &&
			jobOfferId !== null &&
			jobOfferId !== undefined &&
			String(readyJobId) === String(jobOfferId)
		)
	},
	clearReadyToSubmitJobOfferId() {
		safeRemoveSessionItem(KEY_READY_TO_SUBMIT_JOB_OFFER_ID)
		safeRemoveItem(KEY_READY_TO_SUBMIT_JOB_OFFER_ID)
	},

	// A job redirect must survive hops that drop the ?redirect= query (the
	// profile overview forwards unfinished applicants to setup without it),
	// so persist it here exactly as the setup page does on entry.
	rememberJobRedirectIntent(redirectPath) {
		const jobOfferId = getJobOfferIdFromRedirectPath(redirectPath)

		if (!jobOfferId) {
			return null
		}

		this.setRedirectToPath(redirectPath)
		this.setJobOfferId(jobOfferId)
		this.setReadyToSubmitJobOfferId(jobOfferId)

		return jobOfferId
	},

	// Put attribution in the destination so new tabs do not depend on
	// click handlers or an opener's session storage. Vue Router preserves
	// any query and hash already present in the path.
	// The application-source plugin removes this tag on direct/external entry.
	applySourceRoute(path, sourceSurface) {
		return { path, query: { apply_source: sourceSurface } }
	},

	// Carry the claimed visit's source through registration and setup.
	setApplySource(jobOfferId, sourceSurface) {
		if (
			jobOfferId === null ||
			jobOfferId === undefined ||
			typeof sourceSurface !== 'string' ||
			sourceSurface.length === 0
		) {
			return
		}
		safeSetSessionItem(
			KEY_APPLY_SOURCE,
			JSON.stringify({
				jobOfferId: String(jobOfferId),
				sourceSurface,
				createdAt: Date.now()
			})
		)
	},
	getApplySource(jobOfferId) {
		if (jobOfferId === null || jobOfferId === undefined) {
			return null
		}
		const raw = safeGetSessionItem(KEY_APPLY_SOURCE)
		if (!raw) {
			return null
		}
		try {
			const data = JSON.parse(raw)
			// A stamp for job A must not attribute an apply to job B.
			if (
				data &&
				String(data.jobOfferId) === String(jobOfferId) &&
				typeof data.sourceSurface === 'string' &&
				data.sourceSurface.length > 0
			) {
				return data.sourceSurface
			}
		} catch (e) {}
		return null
	},
	// Claims the stamp for the current job page visit: returns the stamped
	// surface and clears it from storage. Bounds attribution to the visit the
	// card click started, so an abandoned visit cannot misattribute a later
	// direct open of the same job. Flows that continue off-page (register,
	// setup) must re-stamp via setApplySource before navigating away.
	claimApplySource(jobOfferId, linkSource = null) {
		const sourceSurface = this.getApplySource(jobOfferId)
		if (sourceSurface) {
			this.clearApplySource()
		}
		// The link is the current navigation; a copied tab may have an older stamp.
		return ['jobs_for_you', 'job_listing'].includes(linkSource)
			? linkSource
			: sourceSurface
	},
	clearApplySource() {
		safeRemoveSessionItem(KEY_APPLY_SOURCE)
	}
}
